import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

export interface UsdVndRate {
  /** How many VND equal 1 USD. */
  vndPerUsd: number;
  fetchedAt: string;
  /** `manual` = admin override, not auto-refreshed. */
  source: 'auto' | 'manual';
}

const RATE_KEY = ['exchange-rate', 'usd-vnd'] as const;

const fetchUsdVndRate = async (): Promise<UsdVndRate> => {
  const response = await axiosInstance.get<{ success: boolean; data: UsdVndRate }>(
    '/v1/admin/exchange-rate'
  );
  return response.data.data;
};

/** Backend caches the rate and refreshes it once a day, so a long client staleTime is enough. */
export const useUsdVndRate = () =>
  useQuery({
    queryKey: RATE_KEY,
    queryFn: fetchUsdVndRate,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

const useRateMutation = <V>(request: (value: V) => Promise<UsdVndRate>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (data) => queryClient.setQueryData(RATE_KEY, data),
  });
};

export const useUpdateUsdVndRate = () =>
  useRateMutation<number>(async (vndPerUsd) => {
    const response = await axiosInstance.put<{ data: UsdVndRate }>('/v1/admin/exchange-rate', {
      vndPerUsd,
    });
    return response.data.data;
  });

/** Discards a manual override and re-fetches the provider rate. */
export const useRefreshUsdVndRate = () =>
  useRateMutation<void>(async () => {
    const response = await axiosInstance.post<{ data: UsdVndRate }>(
      '/v1/admin/exchange-rate/refresh'
    );
    return response.data.data;
  });

export const usdToVnd = (usd: number, vndPerUsd: number) => Math.round(usd * vndPerUsd);

export const vndToUsd = (vnd: number, vndPerUsd: number) =>
  Math.round((vnd / vndPerUsd) * 100) / 100;

export const formatRateHint = (rate?: UsdVndRate) =>
  rate
    ? `1 USD ≈ ${Math.round(rate.vndPerUsd).toLocaleString('en-US')} VND (updated ${new Date(rate.fetchedAt).toLocaleDateString('en-US')})`
    : 'Exchange rate unavailable — enter both prices manually';
